// Messages — read-only monitor for purchasing@cimonstarr.com
// (2026-07-12). Inbound mail lands via api/inbound-email.js (Postmark
// forward + AI triage: category + one-line summary); this page reads,
// marks reviewed/archived, and links emails to items — the foundation
// for "items have purchasing email references" (purchasing_email_items
// junction, real FKs). The app NEVER sends email.
//
// Data loads on demand here (not AppContext boot). Design language:
// hairlines + colored text on white, no fills; search input carries
// the four autocorrect-suppression attributes.
//
// Guard tests: purchasingEmails.test.js (pure), messagesTab.test.jsx
// (wiring).

import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from './AppContext';
import { supabase } from '../lib/supabase';
import { hasAdminAccess } from '../utils';
import {
  rowToPurchasingEmail, rowToEmailAttachment, emailCategoryMeta,
  emailSenderLabel, filterPurchasingEmails, emailDisplayBody,
  searchItemsForEmailLink, suggestItemLinksForEmail,
} from '../lib/purchasingEmails';

const COLOR_TEXT      = '#111111';
const COLOR_MUTED     = '#888880';
const COLOR_BORDER    = '#E0E0DC';
const COLOR_BORDER_DK = '#D8D7D2';
const COLOR_ALERT     = '#D4007A';
const COLOR_PAID      = '#1A5C38';
const COLOR_GROUND    = '#FFFFFF';

const STATUS_TABS = [
  { key: 'new',      label: 'Inbox' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'archived', label: 'Archived' },
  { key: 'all',      label: 'All' },
];

function pill(active) {
  return {
    height: 28, padding: '0 12px', borderRadius: 14,
    border: active ? '1.5px solid ' + COLOR_TEXT : '0.5px solid ' + COLOR_BORDER_DK,
    background: COLOR_GROUND, color: active ? COLOR_TEXT : COLOR_MUTED,
    fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
    cursor: 'pointer', whiteSpace: 'nowrap',
  };
}

function ghostBtn(extra) {
  return Object.assign({
    height: 24, padding: '0 10px', borderRadius: 4,
    border: '0.5px solid ' + COLOR_BORDER_DK, background: COLOR_GROUND,
    color: COLOR_TEXT, fontSize: 10, fontWeight: 500, fontFamily: 'inherit',
    cursor: 'pointer', whiteSpace: 'nowrap',
  }, extra || {});
}

export default function MessagesTab() {
  const ctx = useAppContext();
  const items = ctx.items || [];
  const vendors = ctx.vendors || [];
  const canManage = ctx.isOwner || hasAdminAccess(ctx.userEmail, ctx.firmSettings);

  const [emails, setEmails]       = useState(null);     // null = loading
  const [loadError, setLoadError] = useState('');
  const [statusTab, setStatusTab] = useState('new');
  const [search, setSearch]       = useState('');
  const [openId, setOpenId]       = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [links, setLinks]         = useState([]);       // purchasing_email_items rows for the open email
  const [itemQuery, setItemQuery] = useState('');

  useEffect(function() {
    let alive = true;
    // select('*') on purpose: tolerates the dismissed_item_ids column
    // not existing yet (pre-migration) and picks it up when it does.
    supabase.from('purchasing_emails')
      .select('*')
      .order('received_at', { ascending: false })
      .limit(500)
      .then(function(res) {
        if (!alive) return;
        if (res.error) {
          const msg = String(res.error.message || '');
          setLoadError(msg.indexOf('purchasing_emails') >= 0 || msg.indexOf('does not exist') >= 0
            ? 'The Messages tables aren’t set up yet. Run supabase/migrations/20260712_create_purchasing_emails.sql in the Supabase Dashboard, then reload.'
            : 'Could not load messages: ' + msg);
          setEmails([]);
          return;
        }
        setEmails((res.data || []).map(rowToPurchasingEmail));
      });
    return function() { alive = false; };
  }, []);

  useEffect(function() {
    if (!openId) { setAttachments([]); setLinks([]); return; }
    let alive = true;
    supabase.from('purchasing_email_attachments').select('*').eq('email_id', openId)
      .then(function(res) {
        if (alive) setAttachments(res.error ? [] : (res.data || []).map(rowToEmailAttachment));
      });
    supabase.from('purchasing_email_items').select('*').eq('email_id', openId)
      .then(function(res) {
        if (alive) setLinks(res.error ? [] : (res.data || []));
      });
    setItemQuery('');
    return function() { alive = false; };
  }, [openId]);

  const visible = useMemo(function() {
    return filterPurchasingEmails(emails || [], { status: statusTab, q: search });
  }, [emails, statusTab, search]);

  const counts = useMemo(function() {
    const c = { new: 0, reviewed: 0, archived: 0, all: (emails || []).length };
    (emails || []).forEach(function(e) { if (c[e.status] != null) c[e.status]++; });
    return c;
  }, [emails]);

  const itemsById = useMemo(function() {
    const m = {};
    items.forEach(function(it) { if (it && it.id) m[it.id] = it; });
    return m;
  }, [items]);

  async function setStatus(emailId, status) {
    const res = await supabase.from('purchasing_emails').update({ status: status }).eq('id', emailId);
    if (res.error) { alert('Save failed: ' + res.error.message); return; }
    setEmails(function(prev) {
      return (prev || []).map(function(e) {
        return e.id === emailId ? Object.assign({}, e, { status: status }) : e;
      });
    });
  }

  async function linkItem(itemId) {
    const res = await supabase.from('purchasing_email_items')
      .insert({ email_id: openId, item_id: itemId }).select('*').single();
    if (res.error) { alert('Link failed: ' + res.error.message); return; }
    setLinks(function(prev) { return prev.concat([res.data]); });
    setItemQuery('');
  }

  async function unlinkItem(linkRow) {
    const res = await supabase.from('purchasing_email_items').delete().eq('id', linkRow.id);
    if (res.error) { alert('Unlink failed: ' + res.error.message); return; }
    setLinks(function(prev) { return prev.filter(function(l) { return l.id !== linkRow.id; }); });
  }

  async function dismissSuggestion(email, itemId) {
    const next = (email.dismissedItemIds || []).concat([itemId]);
    const res = await supabase.from('purchasing_emails')
      .update({ dismissed_item_ids: next }).eq('id', email.id);
    if (res.error) {
      const msg = String(res.error.message || '');
      alert(msg.indexOf('dismissed_item_ids') >= 0
        ? 'Dismissals need a one-time migration: run supabase/migrations/20260718_purchasing_emails_dismissed_item_ids.sql in the Supabase Dashboard.'
        : 'Dismiss failed: ' + msg);
      return;
    }
    setEmails(function(prev) {
      return (prev || []).map(function(e) {
        return e.id === email.id ? Object.assign({}, e, { dismissedItemIds: next }) : e;
      });
    });
  }

  async function openAttachment(att) {
    const res = await supabase.storage.from('purchasing-emails')
      .createSignedUrl(att.storagePath, 300);
    if (res.error || !res.data) { alert('Could not open attachment: ' + (res.error && res.error.message)); return; }
    window.open(res.data.signedUrl, '_blank', 'noopener');
  }

  // ── Render ──────────────────────────────────────────────────────────

  const shell = { padding: '20px 18px', background: COLOR_GROUND, minHeight: '100%' };
  const title = (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 16, fontWeight: 500, color: COLOR_TEXT }}>Messages</div>
      <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 4, lineHeight: 1.5 }}>
        Read-only monitor of purchasing@cimonstarr.com. Order confirmations, shipping
        notices, quotes — triaged automatically, linkable to items. Gilda never sends
        email; replies happen in your normal inbox.
      </div>
    </div>
  );

  if (loadError) {
    return <div style={shell}>{title}<div style={{ color: COLOR_ALERT, fontSize: 12, lineHeight: 1.6 }}>{loadError}</div></div>;
  }
  if (emails === null) {
    return <div style={shell}>{title}<div style={{ color: COLOR_MUTED, fontSize: 12 }}>Loading messages…</div></div>;
  }

  const open = openId ? (emails || []).find(function(e) { return e.id === openId; }) : null;

  // ── Detail ──────────────────────────────────────────────────────────
  if (open) {
    const cat = emailCategoryMeta(open.category);
    const linkedIds = links.map(function(l) { return l.item_id; });
    const suggestions = searchItemsForEmailLink(items, itemQuery, linkedIds, 8);
    const autoSuggestions = canManage ? suggestItemLinksForEmail(open, items, vendors, linkedIds) : [];
    return (
      <div style={shell}>
        {title}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <button onClick={function() { setOpenId(null); }} style={ghostBtn()}>← All messages</button>
          {canManage ? (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              {open.status !== 'reviewed' ? (
                <button onClick={function() { setStatus(open.id, 'reviewed'); }} style={ghostBtn({ color: COLOR_PAID, border: '0.5px solid ' + COLOR_PAID })}>✓ Mark reviewed</button>
              ) : null}
              {open.status !== 'archived' ? (
                <button onClick={function() { setStatus(open.id, 'archived'); }} style={ghostBtn({ color: COLOR_MUTED })}>Archive</button>
              ) : (
                <button onClick={function() { setStatus(open.id, 'new'); }} style={ghostBtn()}>Back to inbox</button>
              )}
            </div>
          ) : null}
        </div>

        <div style={{ maxWidth: 760 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: COLOR_TEXT }}>{open.subject || '(no subject)'}</div>
          <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 4 }}>
            {emailSenderLabel(open)} &lt;{open.fromEmail}&gt; · {String(open.receivedAt).slice(0, 16).replace('T', ' ')}
            {open.category ? <span style={{ color: cat.color, marginLeft: 8 }}>· {cat.label}</span> : null}
          </div>
          {open.aiSummary ? (
            <div style={{ fontSize: 11, color: COLOR_TEXT, marginTop: 8, fontStyle: 'italic' }}>
              ✨ {open.aiSummary}
            </div>
          ) : null}

          {/* Linked items */}
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '0.5px solid ' + COLOR_BORDER }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: COLOR_MUTED, marginBottom: 6 }}>
              Linked items
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {links.map(function(l) {
                const it = itemsById[l.item_id];
                return (
                  <span key={l.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                    height: 24, padding: '0 10px', borderRadius: 12,
                    border: '0.5px solid ' + COLOR_BORDER_DK, fontSize: 10, color: COLOR_TEXT }}>
                    {it ? ((it.item || it.productName || 'Item') + (it.itemNumber ? ' · ' + it.itemNumber : '')) : l.item_id}
                    {canManage ? (
                      <span onClick={function() { unlinkItem(l); }} title="Unlink"
                        style={{ cursor: 'pointer', color: COLOR_MUTED }}>×</span>
                    ) : null}
                  </span>
                );
              })}
              {canManage ? (
                <span style={{ position: 'relative' }}>
                  <input
                    type="text" value={itemQuery}
                    onChange={function(e) { setItemQuery(e.target.value); }}
                    placeholder="+ Link an item…"
                    data-testid="link-item-input"
                    autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
                    style={{ height: 24, width: 180, padding: '0 8px',
                      border: '0.5px solid ' + COLOR_BORDER_DK, borderRadius: 12,
                      fontSize: 10, fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                  {suggestions.length ? (
                    <div style={{ position: 'absolute', top: 26, left: 0, zIndex: 5,
                      background: COLOR_GROUND, border: '0.5px solid ' + COLOR_BORDER_DK,
                      borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 260 }}>
                      {suggestions.map(function(it) {
                        return (
                          <div key={it.id}
                            onClick={function() { linkItem(it.id); }}
                            data-testid={'link-item-option-' + it.id}
                            style={{ padding: '6px 10px', fontSize: 11, cursor: 'pointer',
                              borderBottom: '0.5px solid ' + COLOR_BORDER }}>
                            {(it.item || it.productName || 'Item')}
                            <span style={{ color: COLOR_MUTED, marginLeft: 6, fontSize: 10 }}>
                              {it.itemNumber || ''}{it.project ? ' · ' + it.project : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </span>
              ) : null}
              {!links.length && !canManage ? (
                <span style={{ fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic' }}>None yet.</span>
              ) : null}
            </div>
            {autoSuggestions.length ? (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: COLOR_MUTED, marginBottom: 6 }}>
                  ✨ Suggested — click to link
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {autoSuggestions.map(function(s) {
                    const it = s.item;
                    return (
                      <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span
                          onClick={function() { linkItem(it.id); }}
                          data-testid={'suggested-link-' + it.id}
                          title="Link this item"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                            height: 24, padding: '0 10px', borderRadius: 12, cursor: 'pointer',
                            border: '0.5px solid ' + COLOR_ALERT, fontSize: 10, color: COLOR_ALERT }}>
                          + {(it.item || it.productName || 'Item') + (it.itemNumber ? ' · ' + it.itemNumber : '')}
                        </span>
                        <span style={{ fontSize: 10, color: COLOR_MUTED }}>{s.reason}</span>
                        <span
                          onClick={function() { dismissSuggestion(open, it.id); }}
                          data-testid={'suggested-dismiss-' + it.id}
                          title="Dismiss — won't be suggested again for this email"
                          style={{ cursor: 'pointer', color: COLOR_MUTED, fontSize: 11 }}>×</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>

          {/* Attachments */}
          {attachments.length ? (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '0.5px solid ' + COLOR_BORDER }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: COLOR_MUTED, marginBottom: 6 }}>
                Attachments
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {attachments.map(function(a) {
                  return (
                    <button key={a.id} onClick={function() { openAttachment(a); }} style={ghostBtn()}>
                      ↓ {a.filename}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Body */}
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '0.5px solid ' + COLOR_BORDER,
            fontSize: 12, color: COLOR_TEXT, lineHeight: 1.6, whiteSpace: 'pre-wrap',
            fontFamily: 'inherit', wordBreak: 'break-word' }}>
            {emailDisplayBody(open) || <span style={{ color: COLOR_MUTED, fontStyle: 'italic' }}>(empty body)</span>}
          </div>
        </div>
      </div>
    );
  }

  // ── List ────────────────────────────────────────────────────────────
  return (
    <div style={shell}>
      {title}

      <div style={{ marginBottom: 12 }}>
        <input
          type="text" value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          placeholder="Search sender, subject, contents…"
          autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
          style={{ width: '100%', maxWidth: 480, height: 32, padding: '0 12px',
            border: '0.5px solid ' + COLOR_BORDER_DK, borderRadius: 6,
            background: COLOR_GROUND, color: COLOR_TEXT,
            fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {STATUS_TABS.map(function(t) {
          return (
            <button key={t.key} onClick={function() { setStatusTab(t.key); }} style={pill(statusTab === t.key)}>
              {t.label}{counts[t.key] ? ' · ' + counts[t.key] : ''}
            </button>
          );
        })}
      </div>

      {emails.length === 0 ? (
        <div style={{ maxWidth: 620, padding: '24px 26px',
          border: '0.5px dashed ' + COLOR_BORDER, borderRadius: 6,
          fontSize: 12, color: COLOR_TEXT, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>No messages yet — one connection step remains.</div>
          The page is ready; it just needs purchasing@ mail forwarded to it:
          <ol style={{ margin: '8px 0 8px 18px', padding: 0 }}>
            <li>Create a free Postmark account → add an <b>Inbound</b> server → copy its inbound email address.</li>
            <li>In Vercel, add an env var <b>INBOUND_EMAIL_TOKEN</b> (any long random string) and redeploy.</li>
            <li>In Postmark's inbound settings, set the webhook URL to<br />
              <code style={{ fontSize: 11 }}>https://app.cimonstarr.com/api/inbound-email?token=&lt;that token&gt;</code></li>
            <li>In Gmail (purchasing@ account) → Settings → Forwarding: auto-forward a copy of all mail to the Postmark inbound address. Gmail keeps its copy — nothing changes about how you read or reply to email.</li>
          </ol>
          From then on every email lands here within seconds, triaged and ready to link to items.
        </div>
      ) : visible.length === 0 ? (
        <div style={{ padding: '32px 0', textAlign: 'center', color: COLOR_MUTED,
          fontSize: 12, fontStyle: 'italic' }}>
          Nothing {statusTab === 'all' ? '' : 'in ' + statusTab}{search.trim() ? ' matching "' + search.trim() + '"' : ''}.
        </div>
      ) : (
        <table style={{ width: '100%', maxWidth: 980, borderCollapse: 'collapse' }}>
          <tbody>
            {visible.map(function(e) {
              const cat = emailCategoryMeta(e.category);
              return (
                <tr key={e.id}
                  onClick={function() { setOpenId(e.id); }}
                  style={{ cursor: 'pointer' }}>
                  <td style={{ padding: '10px 8px', borderBottom: '0.5px solid ' + COLOR_BORDER,
                    whiteSpace: 'nowrap', fontSize: 11, verticalAlign: 'top',
                    fontWeight: e.status === 'new' ? 500 : 400, color: COLOR_TEXT, width: 170 }}>
                    {emailSenderLabel(e)}
                  </td>
                  <td style={{ padding: '10px 8px', borderBottom: '0.5px solid ' + COLOR_BORDER,
                    fontSize: 11, verticalAlign: 'top' }}>
                    <span style={{ fontWeight: e.status === 'new' ? 500 : 400, color: COLOR_TEXT }}>
                      {e.subject || '(no subject)'}
                    </span>
                    {e.aiSummary ? (
                      <span style={{ color: COLOR_MUTED, marginLeft: 8 }}>— {e.aiSummary}</span>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 8px', borderBottom: '0.5px solid ' + COLOR_BORDER,
                    whiteSpace: 'nowrap', fontSize: 10, verticalAlign: 'top',
                    color: cat.color, width: 120 }}>
                    {e.category ? cat.label : ''}
                  </td>
                  <td style={{ padding: '10px 8px', borderBottom: '0.5px solid ' + COLOR_BORDER,
                    whiteSpace: 'nowrap', fontSize: 10, color: COLOR_MUTED,
                    verticalAlign: 'top', width: 90, textAlign: 'right' }}>
                    {String(e.receivedAt).slice(0, 10)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
